import { useId } from 'react';
import { TRPCClientError } from '@trpc/client';
import {
  ADDRESS_MESSAGES,
  DEFAULT_COUNTRY,
  SUPPORTED_COUNTRIES,
  countryName,
  formatAddressLines,
  normalizePostalCode,
  normalizeRegion,
  postalLabel,
  regionLabel,
  regionsFor,
} from '@buildbee/domain';
import { Field, Input, Select, cn } from '@buildbee/ui';

export type AddressValue = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export type AddressErrors = Partial<Record<'line1' | 'city' | 'region' | 'postalCode' | 'country' | 'form', string>>;

/** Example postal code for the input hint. */
export function postalExample(country: string | null | undefined): string {
  const c = country?.toUpperCase();
  if (c === 'CA') return 'M5V 2T6';
  if (c === 'US') return '98101';
  return c ? '' : 'M5V 2T6 or 98101';
}

/**
 * The address to send to the API: null when nothing was entered, otherwise every field filled in
 * and the country defaulted (Canada unless the caller says otherwise).
 */
export function cleanAddress(a: AddressValue | null | undefined, defaultCountry: string = DEFAULT_COUNTRY) {
  if (!a || (!a.line1?.trim() && !a.city?.trim() && !a.region?.trim() && !a.postalCode?.trim())) return null;
  return {
    line1: a.line1?.trim() ?? '',
    line2: a.line2?.trim() ?? '',
    city: a.city?.trim() ?? '',
    region: a.region?.trim() ?? '',
    postalCode: a.postalCode?.trim() ?? '',
    country: (a.country || defaultCountry).trim().slice(0, 2).toUpperCase(),
  };
}

const REGION_MESSAGES = new Set<string>(Object.values(ADDRESS_MESSAGES).map((m) => m.region));
const POSTAL_MESSAGES = new Set<string>(Object.values(ADDRESS_MESSAGES).map((m) => m.postalCode));

/**
 * Address validation errors from a tRPC error, placed on their fields. The API's zod errors are
 * flattened by top-level key (`address`), so each message is matched to the field it is about.
 */
export function addressErrors(error: unknown, key = 'address'): AddressErrors {
  if (!(error instanceof TRPCClientError)) return {};
  const fieldErrors = (error.data as { zodError?: { fieldErrors?: Record<string, string[] | undefined> } } | undefined)?.zodError?.fieldErrors;
  const out: AddressErrors = {};
  for (const message of fieldErrors?.[key] ?? []) {
    const field = REGION_MESSAGES.has(message) ? 'region' : POSTAL_MESSAGES.has(message) ? 'postalCode' : /country/i.test(message) ? 'country' : 'form';
    out[field] ??= message;
  }
  return out;
}

/**
 * The one address editor: street, city, country (Canada or the United States), a province/state
 * dropdown for the chosen country and a postal/ZIP code. Labels follow the country ("Province",
 * "Postal code" for Canada; "State", "ZIP code" for the US; both while no country is chosen).
 * `defaultCountry` applies when the value has no country (pass null to start with none chosen).
 */
export function AddressFields({
  value,
  onChange,
  errors,
  defaultCountry = DEFAULT_COUNTRY,
  legend = 'Address',
  disabled,
  className,
}: {
  value: AddressValue;
  onChange: (a: AddressValue) => void;
  errors?: AddressErrors;
  defaultCountry?: string | null;
  legend?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const country = (value.country || defaultCountry || '').toUpperCase();
  const regions = regionsFor(country);
  const region = value.region ?? '';
  const regionCode = country ? (normalizeRegion(country, region) ?? region) : region;
  const set = (patch: AddressValue) => onChange({ ...value, country: country || value.country, ...patch });

  const countryOptions: { value: string; label: string }[] = SUPPORTED_COUNTRIES.map((c) => ({ value: c.code, label: c.name }));
  if (country && !countryOptions.some((o) => o.value === country)) countryOptions.push({ value: country, label: countryName(country) ?? country });

  const changeCountry = (next: string) => {
    const keepRegion = next && region ? normalizeRegion(next, region) : null;
    onChange({ ...value, country: next || null, region: keepRegion ?? '' });
  };

  const regionField = (() => {
    const props = { id: `${id}-region`, disabled, 'aria-invalid': errors?.region ? true : undefined } as const;
    if (!country) {
      // No country yet: provinces and states together; choosing one also sets the country.
      return (
        <Select
          {...props}
          value={region ? `${normalizeRegion('CA', region) ? 'CA' : 'US'}:${region}` : ''}
          onChange={(e) => {
            const [c, code] = e.target.value.split(':');
            onChange({ ...value, country: c || null, region: code ?? '' });
          }}
        >
          <option value="">Choose…</option>
          {SUPPORTED_COUNTRIES.map((c) => (
            <optgroup key={c.code} label={c.name}>
              {regionsFor(c.code).map((r) => (
                <option key={r.code} value={`${c.code}:${r.code}`}>
                  {r.name}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      );
    }
    if (!regions.length) return <Input {...props} value={region} onChange={(e) => set({ region: e.target.value })} />;
    const known = regions.some((r) => r.code === regionCode);
    return (
      <Select {...props} value={regionCode} onChange={(e) => set({ region: e.target.value })}>
        <option value="">Choose…</option>
        {!known && regionCode ? <option value={regionCode}>{regionCode}</option> : null}
        {regions.map((r) => (
          <option key={r.code} value={r.code}>
            {r.name}
          </option>
        ))}
      </Select>
    );
  })();

  return (
    <fieldset className={cn('grid gap-3 sm:grid-cols-6', className)}>
      {legend ? <legend className="mb-1 text-sm font-medium">{legend}</legend> : null}
      <Field label="Street" htmlFor={`${id}-line1`} error={errors?.line1} className="sm:col-span-6">
        <Input id={`${id}-line1`} autoComplete="street-address" disabled={disabled} value={value.line1 ?? ''} onChange={(e) => set({ line1: e.target.value })} />
      </Field>
      <Field label="City" htmlFor={`${id}-city`} error={errors?.city} className="sm:col-span-3">
        <Input id={`${id}-city`} autoComplete="address-level2" disabled={disabled} value={value.city ?? ''} onChange={(e) => set({ city: e.target.value })} />
      </Field>
      <Field label="Country" htmlFor={`${id}-country`} error={errors?.country} className="sm:col-span-3">
        <Select id={`${id}-country`} autoComplete="country" disabled={disabled} value={country} onChange={(e) => changeCountry(e.target.value)}>
          {!country ? <option value="">Choose…</option> : null}
          {countryOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={regionLabel(country || null)} htmlFor={`${id}-region`} error={errors?.region} className="sm:col-span-3">
        {regionField}
      </Field>
      <Field label={postalLabel(country || null)} htmlFor={`${id}-postal`} error={errors?.postalCode} className="sm:col-span-3">
        <Input
          id={`${id}-postal`}
          autoComplete="postal-code"
          disabled={disabled}
          placeholder={postalExample(country || null)}
          aria-invalid={errors?.postalCode ? true : undefined}
          value={value.postalCode ?? ''}
          onChange={(e) => set({ postalCode: e.target.value })}
          onBlur={(e) => {
            const normalized = normalizePostalCode(country || null, e.target.value);
            if (normalized && normalized !== e.target.value) set({ postalCode: normalized });
          }}
        />
      </Field>
      {errors?.form ? <p className="text-xs text-destructive sm:col-span-6">{errors.form}</p> : null}
    </fieldset>
  );
}

/**
 * An address as mailing lines (Canada Post / USPS order, e.g. "Toronto ON  M5V 2T6"), one per
 * line with spacing kept. The country is added for addresses outside `homeCountry`. Null when empty.
 */
export function AddressLines({ address, homeCountry, className }: { address: AddressValue | null | undefined; homeCountry?: string; className?: string }) {
  const lines = formatAddressLines(address, { homeCountry });
  if (!lines.length) return null;
  return (
    <span className={cn('block', className)}>
      {lines.map((l, i) => (
        <span key={i} className="block whitespace-pre-wrap">
          {l}
        </span>
      ))}
    </span>
  );
}
